/**
 * @file usePutStock.ts
 * @description Данные диалога «Положить товар» (phase19.23, A4).
 *
 * Кладовщик ищет фасовку по складу ячейки и кладёт её партии в ячейку.
 * Источник — остатки склада (`GET /admin/wms/stock`): там же приходит текущая
 * ячейка партии (`lot.bin`), поэтому предупреждение «партия переедет из A-01-01»
 * считается без дополнительных запросов.
 *
 * Размещение = перестановка партии (`POST /admin/wms/lots/:id/move-bin`):
 * количество и склад не меняются, меняется адрес хранения (W14). Партии, уже
 * лежащие в целевой ячейке, пропускаем — лишний запрос ничего не даёт.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useNotify } from "../../ui/NotifyProvider";
import { binsApi } from "../../../lib/api-bins";
import { formatSkuName, stockApi, type StockLevelDto } from "../../../lib/api-wms";
import { wmsErrorText } from "../../../lib/wms-error-text";

/** Партия фасовки на складе: сколько лежит и где сейчас стоит. */
export interface PutStockLot {
  lotId: string;
  batchNumber: string;
  expiresAt: string | null;
  quantity: number;
  binId: string | null;
  binCode: string | null;
}

/** Фасовка с остатком: строка первого шага диалога. */
export interface PutStockSku {
  skuId: string;
  name: string;
  code: string;
  totalQuantity: number;
  lots: PutStockLot[];
}

/** Остатки без партии разместить нельзя — ячейка адресует именно партию. */
function toLot(row: StockLevelDto): PutStockLot | null {
  if (!row.lot) return null;
  return {
    lotId: row.lot.id,
    batchNumber: row.lot.batchNumber,
    expiresAt: row.lot.expiresAt,
    quantity: Number(row.quantity),
    binId: row.lot.bin?.id ?? null,
    binCode: row.lot.bin?.code ?? null,
  };
}

export function usePutStock(
  warehouseId: string,
  targetBinId: string,
  targetBinCode: string,
) {
  const notify = useNotify();
  const [rows, setRows] = useState<StockLevelDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  // Счётчик перечитывания: после частичного переноса остатки на экране
  // устарели, а править их руками нельзя — перечитываем с сервера.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    stockApi
      .list({ warehouseId, onlyPositive: true })
      .then((result) => {
        if (cancelled) return;
        // Реестр остатков отдаёт строки вместе с признаком «показано не всё»;
        // диалогу «Положить товар» нужен только список.
        setRows(result.items);
        setError(null);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(wmsErrorText(err, "Не удалось загрузить остатки"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [warehouseId, reloadKey]);

  /** Остатки склада, свёрнутые в фасовки с партиями. */
  const skus = useMemo<PutStockSku[]>(() => {
    const map = new Map<string, PutStockSku>();
    for (const row of rows) {
      const lot = toLot(row);
      if (!lot) continue;
      const existing = map.get(row.skuId);
      if (existing) {
        existing.totalQuantity += lot.quantity;
        existing.lots.push(lot);
        continue;
      }
      map.set(row.skuId, {
        skuId: row.skuId,
        name: formatSkuName(row.sku),
        code: row.sku?.code ?? row.skuId,
        totalQuantity: lot.quantity,
        lots: [lot],
      });
    }
    return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, "ru"));
  }, [rows]);

  /**
   * Переставить выбранные партии в целевую ячейку.
   *
   * Переносы идут по одной партии, поэтому сбой на середине — это не «ничего
   * не произошло»: часть партий уже переехала. Считаем успешные, о неудачных
   * говорим поимённо и возвращаем false — диалог остаётся открытым, чтобы
   * кладовщик видел, что осталось. `true` = можно закрывать.
   */
  const place = useCallback(
    async (lots: PutStockLot[]): Promise<boolean> => {
      const moving = lots.filter((lot) => lot.binId !== targetBinId);
      if (moving.length === 0) {
        // Не ошибка и не успех: класть нечего, всё уже на месте.
        notify.info(`Партии уже лежат в ${targetBinCode}`);
        return true;
      }
      setSaving(true);
      let moved = 0;
      // Номер документа первой удачной перестановки — его показываем в тосте
      // при одиночном переносе (phase19.23 этап B: быстрое действие пишет MV).
      let transferNumber: string | null = null;
      const failed: { batchNumber: string; reason: string }[] = [];
      try {
        for (const lot of moving) {
          try {
            const result = await binsApi.moveLot(lot.lotId, targetBinId);
            transferNumber = transferNumber ?? result.transferNumber ?? null;
            moved += 1;
          } catch (err) {
            failed.push({
              batchNumber: lot.batchNumber,
              reason: wmsErrorText(err, "ошибка сервера"),
            });
          }
        }
      } finally {
        setSaving(false);
      }

      if (moved > 0) setReloadKey((n) => n + 1);

      if (failed.length === 0) {
        const single = moving.length === 1 ? moving[0] : null;
        const doc = transferNumber ? ` · документ ${transferNumber}` : "";
        notify.success(
          single
            ? `Партия ${single.batchNumber} размещена в ${targetBinCode}${doc}`
            : `Размещено партий: ${moving.length}`,
        );
        return true;
      }

      const first = failed[0];
      const rest = failed.length > 1 ? ` и ещё партий: ${failed.length - 1}` : "";
      const reason = first ? `партия ${first.batchNumber} — ${first.reason}` : "";
      notify.error(
        `Перенесено ${moved} из ${moving.length}. Не удалось: ${reason}${rest}`,
      );
      return false;
    },
    [targetBinId, targetBinCode, notify],
  );

  return { skus, loading, error, saving, place };
}
